import { fromIso, toIso } from '@map/shared';
const MS_PER_DAY = 86_400_000;
export function daysBetween(from, to) {
    const a = Date.UTC(from.year, from.month - 1, from.day);
    const b = Date.UTC(to.year, to.month - 1, to.day);
    return Math.round((b - a) / MS_PER_DAY);
}
/**
 * A single task's contribution, 0 to 1.
 *
 * The curve degrades quickly at first and then flattens: the difference between
 * one day late and four days late matters far more than the difference between
 * thirty and sixty, by which point the region is already as red as it gets.
 */
export function deadlineScore(task, today, options = {}) {
    if (task.status !== 'open')
        return 0;
    if (!task.targetDate)
        return 0;
    const approachWindow = options.approachWindow ?? 7;
    const saturation = options.saturation ?? 21;
    const daysLate = daysBetween(fromIso(task.targetDate), today);
    if (daysLate < -approachWindow)
        return 0;
    if (daysLate <= 0) {
        // Approaching: rises gently to 0.3 on the day it is due.
        const progress = (daysLate + approachWindow) / approachWindow;
        return 0.3 * progress * progress;
    }
    // Late: 0.3 on the day, then a saturating curve toward 1.
    const overdue = 1 - Math.exp((-2.2 * daysLate) / saturation);
    const scored = 0.3 + 0.7 * overdue;
    // Something with a genuine external deadline deserves to be redder sooner.
    return Math.min(1, task.hardDue ? scored * 1.15 : scored);
}
/** The worst open task decides, not the average: ten fine tasks do not excuse one badly late one. */
export function deadlineScoreFor(tasks, today, options) {
    let worst = 0;
    for (const task of tasks)
        worst = Math.max(worst, deadlineScore(task, today, options));
    return worst;
}
/**
 * How badly an area has been left alone, measured against its own rhythm.
 *
 * Capped deliberately: an untended area should look tired, not on fire. A
 * genuinely overdue obligation must always be able to out-rank it.
 */
export function neglectScore(daysSinceActivity, cadenceDays, options = {}) {
    const ceiling = options.ceiling ?? 0.62;
    const cadence = cadenceDays ?? options.defaultCadenceDays ?? 30;
    if (daysSinceActivity === null)
        return 0;
    if (daysSinceActivity <= cadence)
        return 0;
    const overdueRatio = (daysSinceActivity - cadence) / cadence;
    return Math.min(ceiling, ceiling * (1 - Math.exp(-overdueRatio)));
}
export function combinedScore(signals, today, options = {}) {
    const deadline = deadlineScoreFor(signals.tasks, today, options);
    const daysSince = signals.lastActivityAt === null
        ? null
        : daysBetween(civilFromIsoInstant(signals.lastActivityAt), today);
    const neglect = neglectScore(daysSince, signals.reviewCadenceDays, options);
    return { deadline, neglect, combined: Math.max(deadline, neglect) };
}
/** An instant, reduced to the civil day it falls on in the scoring zone. */
export function civilFromIsoInstant(iso, timeZone = 'Europe/London') {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date(iso));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    return { year: get('year'), month: get('month'), day: get('day') };
}
export { toIso };
//# sourceMappingURL=scoring.js.map